import React, { useEffect, useRef, useState } from "react";
import { getSocket, onSocketDisconnect } from "../../socket";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { RiUserAddFill } from "react-icons/ri";
import { MdKeyboardVoice } from "react-icons/md";
import { AiOutlineAudioMuted } from "react-icons/ai";
import { MdCallEnd } from "react-icons/md";
import { Avatar } from "@mui/material";
import { playAudioWithGlobalContext } from "../../utils/audioContext";
import { obs, ObsEvent, LatencyTracker } from "../../services/observability";
import Cookie from "../../utils/Cookie";
import useGlobalStore from "../../hooks/useGlobalStore";
import PayrollModal from "../Modals/ShowPayrollModal";

// PCM 24kHz mono 16-bit: 48,000 bytes/sec. The backend relays ElevenLabs'
// raw audio bursts immediately with no smoothing, so this is the frontend's
// own pre-buffer -- how much to collect before scheduling a turn's first
// chunk, trading a small amount of latency for not stuttering on startup.
const PCM_24K_MONO_16BIT_BYTES_PER_SEC = 24_000 * 2;
const FIRST_CHUNK_MIN_BYTES = Math.floor(PCM_24K_MONO_16BIT_BYTES_PER_SEC * 0.2); // 200ms

const VoiceChat = ({ chatId, userId, name, profilePic, isBackPressed }) => {
    const { TOGGLE_PAYROLL_MODAL, toggleForModal } = useGlobalStore();

    const socket = getSocket();
    const navigate = useNavigate();
    const callStatus = Cookie.getCookie('call_start');

    const [micReady, setMicReady] = useState(false);
    const [dgReady, setDgReady] = useState(false);
    const [mute, setMute] = useState(false);
    // Ref mirror of `mute`: the worklet callback is created once, so reading state directly
    // is a stale closure that never sees mute flip and keeps streaming (and billing) audio.
    const muteRef = useRef(false);
    const [isRecording, setIsRecording] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [isConnecting, setIsConnecting] = useState(true);
    const [permissionStatus, setPermissionStatus] = useState("pending");
    const [callPhase, setCallPhase] = useState("requesting_permissions");

    // Refs for persistent objects
    const audioStreamRef = useRef(null);
    const micCtxRef = useRef(null);
    const micProcessorRef = useRef(null);
    const micSourceRef = useRef(null);
    const deepgramSocketRef = useRef(null);

    const ttsCtxRef = useRef(null);
    const ttsQueueRef = useRef([]);
    const ttsPlayingRef = useRef(false);
    const ttsCurrentSourceRef = useRef(null);
    /** Serializes decodeAudioData for the non-PCM fallback path (ordered decode). */
    const decodingQueueRef = useRef(Promise.resolve());

    // PCM streaming state: nextStartTime is the AudioContext timestamp at which
    // the next scheduled buffer should start, so consecutive PCM chunks play
    // gaplessly. scheduledSources holds every BufferSource we've scheduled but
    // not yet ended, so a barge-in can stop them all at once. currentTurnId
    // resets the schedule on a fresh turn so we don't accumulate stale future
    // start times after an interrupt. ttsStreamEnded mirrors the backend
    // `tts_audio_end` so we only fire turn-end after the last buffer finishes.
    const nextStartTimeRef = useRef(0);
    const scheduledSourcesRef = useRef([]);
    const currentTurnIdRef = useRef(null);
    const ttsStreamEndedRef = useRef(false);

    // First-chunk pre-buffer (see schedulePcmChunk): accumulates raw PCM
    // bytes for a turn's first piece until FIRST_CHUNK_MIN_BYTES is reached,
    // so the first scheduled buffer has a safe amount of lead time instead
    // of a razor-thin one. Resets per turn (see queueTtsChunk).
    const firstChunkBufferRef = useRef(new Uint8Array(0));
    const firstChunkPendingRef = useRef(true);

    const isAISpeakingRef = useRef(false);

    // Sound effects
    const ringingAudioRef = useRef(null);
    const pickupAudioRef = useRef(null);
    const hangupAudioRef = useRef(null);

    // Keep-alive ref
    const keepAliveIntervalRef = useRef(null);
    const visibilityEndTimerRef = useRef(null);
    const stopAllRef = useRef(null);
    const isStoppingRef = useRef(false);

    // Latency tracking refs
    const lastAudioByteTimeRef = useRef(null);
    const deepgramConnectTrackerRef = useRef(null);
    const firstTtsChunkReceivedRef = useRef(false);



    // Set call context when entering audio call
    useEffect(() => {
        if (name && userId) {
            obs.setCallContext(userId, name, 'audio');
        }
        return () => {
            obs.clearCallContext();
        };
    }, [name, userId]);

    // Sound Effect Logic
    useEffect(() => {
        let timerInterval;
        let ringTimeoutRef = null;

        const playRinging = async () => {
            try {
                if (ringingAudioRef.current) {
                    ringingAudioRef.current.stop();
                    ringingAudioRef.current = null;
                }

                const audioControl = await playAudioWithGlobalContext("/sounds/ringing2.mp3", {
                    loop: true
                });
                ringingAudioRef.current = audioControl;
                console.log("[VoiceChat] Ringing started");

                // ringTimeoutRef = setTimeout(() => {
                //     console.log("[VoiceChat] Call timeout - ending call");
                //     stopAll();
                // }, 20000);
            } catch (err) {
                console.error("Ringing audio play error:", err);
            }
        };

        if (!isRecording && isConnecting && permissionStatus === "granted") {
            if (callStatus === 'true') {
                Cookie.deleteCookie('call_start');
                stopAll()
                return; // ← stop here, don't start a new call
            }
            playRinging();
        } else if (isRecording) {
            if (ringTimeoutRef) {
                clearTimeout(ringTimeoutRef);
                ringTimeoutRef = null;
            }

            console.log("[VoiceChat] Call picked up, stopping ringing");
            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
            }

            timerInterval = setInterval(() => {
                setCallDuration((prev) => prev + 1);
            }, 1000);

            // Start keep-alive interval for stale session detection
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
            }
            keepAliveIntervalRef.current = setInterval(() => {
                socket.emit('keep_session_alive', {
                    creator_id: userId,
                    session_type: 'voice'
                });
            }, 5000);
            // Send initial keep-alive immediately
            socket.emit('keep_session_alive', { creator_id: userId, session_type: 'voice' });

            socket.emit("get_greeting", {
                chat_id: chatId,
                creator_id: userId,
            });
        }

        return () => {
            if (ringTimeoutRef) {
                clearTimeout(ringTimeoutRef);
            }

            console.log("[VoiceChat] Cleanup - stopping all sounds");
            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
            }
            if (pickupAudioRef.current) {
                pickupAudioRef.current.stop();
                pickupAudioRef.current = null;
            }
            if (timerInterval) clearInterval(timerInterval);
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
        };
    }, [isRecording, isConnecting, permissionStatus]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    // STEP 1 — get mic & request token
    const requestMic = async () => {
        try {
            setPermissionStatus("pending");
            setCallPhase("requesting_permissions");
            const audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                },
            });

            audioStreamRef.current = audioStream;
            setMicReady(true);
            setPermissionStatus("granted");
            setCallPhase("starting_call");
            // Create and resume AudioContext IMMEDIATELY after getUserMedia
            if (!micCtxRef.current) {
                micCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({
                    sampleRate: 16000,
                });
                console.log('[VoiceChat] AudioContext created early', micCtxRef.current.state);
            }

            // Resume immediately while still in user gesture chain
            if (micCtxRef.current.state === "suspended") {
                try {
                    await micCtxRef.current.resume();
                    console.log('[VoiceChat] AudioContext resumed early SUCCESS', micCtxRef.current.state);
                } catch (resumeErr) {
                    console.error('[VoiceChat] AudioContext resume early FAILED', resumeErr?.message);
                }
            }

            socket.emit("request_audio_token", { creator_id: userId });
            return true;
        } catch (err) {
            console.error("Mic error", err);
            setPermissionStatus("denied");
            setCallPhase("ending");
            toast.error("Microphone denied");
            return false;
        }
    };

    // STEP 2 — connect Deepgram
    useEffect(() => {
        // Guard: during a connection blip getSocket() can be null; socket.on on null
        // crashed the whole call page (blank screen) instead of failing gracefully.
        if (!socket) return;

        const handleAudioTokenResponse = (data) => {
            if (data?.token) connectToDeepgram(data.token);
        };

        const handleTtsAudioChunk = (data) => {
            const { audio, turn_id, format } = data || {};

            // Drop chunks for any turn the user has barged or moved past.
            // Without this, in-flight chunks from the previous turn arrive after
            // the local queue has been cleared and play over the user.
            if (!obs.isValidAiTurn(turn_id)) {
                return;
            }

            // Track first TTS chunk for e2e latency
            if (!firstTtsChunkReceivedRef.current) {
                firstTtsChunkReceivedRef.current = true;

                obs.emitWithLatency(ObsEvent.E2E_AUDIO_TTFB, 'e2e_ttfb_ms', {
                    turn_id: turn_id || obs.getTurn().turn_id,
                    status: 'success'
                });
            }

            queueTtsChunk(audio, { format, turn_id });
        };

        const handleTtsAudioEnd = () => {
            // Flush any audio still sitting in the first-chunk pre-buffer
            // (a turn shorter than FIRST_CHUNK_MIN_BYTES, e.g. a quick
            // greeting, never reaches the threshold on its own) BEFORE the
            // "anything still playing" check below, or that trailing audio
            // would silently never play and isAISpeaking could flip false
            // while it's still pending.
            flushPendingFirstChunk();
            ttsStreamEndedRef.current = true;
            if (
                scheduledSourcesRef.current.length === 0 &&
                isAISpeakingRef.current
            ) {
                isAISpeakingRef.current = false;
                console.log("AI finished speaking");
                obs.endTurn();
            }
        };

        const handleTtsComplete = (payload) => {
            console.log("AI voice response: ", payload?.text);
        };

        // Handle call ended event from server
        const handleAudioCallEnded = (data) => {
            console.log("[VoiceChat] audio_call_ended received, status:", data?.status);
            if (data?.reason) toast.error(data.reason);
            if (data?.status === "limit_exceeded") {
                stopAll('limit_exceeded');
                toggleForModal('TOGGLE_PAYROLL_MODAL')
                return
            }
            stopAll();
        };

        socket.on("audio_token_response", handleAudioTokenResponse);
        socket.on("tts_audio_chunk", handleTtsAudioChunk);
        socket.on("tts_audio_end", handleTtsAudioEnd);
        socket.on("tts_complete", handleTtsComplete);
        socket.on("audio_call_ended", handleAudioCallEnded);

        return () => {
            socket.off("audio_token_response", handleAudioTokenResponse);
            socket.off("tts_audio_chunk", handleTtsAudioChunk);
            socket.off("tts_audio_end", handleTtsAudioEnd);
            socket.off("tts_complete", handleTtsComplete);
            socket.off("audio_call_ended", handleAudioCallEnded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectToDeepgram = (token) => {
        if (deepgramSocketRef.current && deepgramSocketRef.current.readyState !== WebSocket.CLOSED) {
            console.log("[VoiceChat] Deepgram already connected, skipping duplicate connection");
            return;
        }

        deepgramConnectTrackerRef.current = new LatencyTracker().start();

        console.log("[VoiceChat] Creating new Deepgram connection");
        const wsUrl =

            "wss://api.deepgram.com/v2/listen?model=flux-general-en&encoding=linear16&sample_rate=16000&eot_threshold=0.85&eot_timeout_ms=8000";
        const dg = new WebSocket(wsUrl, ["bearer", token]);
        deepgramSocketRef.current = dg;

        dg.onopen = () => {
            setDgReady(true);
            const connectionMs = deepgramConnectTrackerRef.current?.elapsed() || null;
            console.log("[VoiceChat] Connected to Deepgram");

            obs.emit(ObsEvent.DEEPGRAM_CONNECTED, {
                connection_ms: connectionMs,
                status: 'success'
            });

            setIsConnecting(false);
            setCallPhase("connected");

            console.log("[VoiceChat] Deepgram connected - stopping ringing, ref:", ringingAudioRef.current);
            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
                console.log("[VoiceChat] Ringing stopped via Deepgram connection");
            }

            // Log audio call started
            obs.emit(ObsEvent.AUDIO_CALL_STARTED, {
                status: 'success'
            });
        };

        dg.onmessage = (evt) => {
            try {
                const data = JSON.parse(evt.data);
                if (data.type === "TurnInfo" && data.event === "StartOfTurn") {
                    handleStartOfTurn();
                }

                if (data.type === "TurnInfo" && data.event === "EndOfTurn") {
                    const transcript = data.transcript?.trim();
                    if (transcript) {
                        handleEndOfTurn(transcript);
                    }
                }
            } catch (e) {
                // not JSON or not Deepgram event
            }
        };

        dg.onerror = (e) => {
            console.error("Deepgram WS error", e);
            obs.emit(ObsEvent.DEEPGRAM_CONNECTION_ERROR, {
                error_message: e?.message || 'Unknown error',
                status: 'error'
            });
            toast.error("Call failed. Please try again.");
            stopAllRef.current?.("call_failed");
            socket.emit('get_message', {
                creator_id: userId,
                chat_id: chatId,
                scenario: 'call_failed'
            })
        };

        dg.onclose = (e) => {
            console.log("Deepgram closed", e?.code, e?.reason);
            // Code 1000 = normal closure, 1001 = going away (page close) - these are expected
            const isExpected = e?.code === 1000 || e?.code === 1001;
            obs.emit(ObsEvent.DEEPGRAM_DISCONNECTED, {
                code: e?.code,
                reason: e?.reason,
                was_clean: e?.wasClean,
                status: isExpected ? 'info' : 'error'
            });
            if (!isExpected && !isStoppingRef.current) {
                toast.error("Call disconnected");
                stopAllRef.current?.("call_failed");
            }
        };
    };
    useEffect(() => {
        if (dgReady) {
            console.log("[Fix] Both ready → starting pipeline");
            try {
                const playHangup = async () => {
                    const audioControl = await playAudioWithGlobalContext("/sounds/pickup.mp3")
                    pickupAudioRef.current = audioControl;
                }
                if (!pickupAudioRef.current) {
                    playHangup()
                }
            } catch (err) {
                console.log(err)
            }
            startMicPipeline();
        }
    }, [dgReady]);
    // STEP 3 — Mic pipeline
    const startMicPipeline = async () => {
        let waited = 0;
        while (!audioStreamRef.current || !audioStreamRef.current.active) {
            if (waited >= 5000) {
                console.error("No mic stream after waiting");
                return;
            }
            await new Promise(r => setTimeout(r, 100));
            waited += 100;
        }

        if (micProcessorRef.current) {
            console.log("[Fix] Mic pipeline already exists");
            return;
        }
        if (!micCtxRef.current) {
            micCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 16000,
            });
        }

        const micCtx = micCtxRef.current;

        if (micCtx.state === "suspended") {
            try {
                await micCtx.resume();
            } catch (err) {
                console.error("AudioContext resume failed:", err);
            }
        }

        if (!micCtx.audioWorkletLoaded) {
            try {
                await micCtx.audioWorklet.addModule("/mic-processor.js");
                micCtx.audioWorkletLoaded = true;
            } catch (err) {
                console.error("AudioWorklet load failed:", err);
                return;
            }
        }

        if (!micSourceRef.current) {
            micSourceRef.current = micCtx.createMediaStreamSource(audioStreamRef.current);
        }

        if (!micProcessorRef.current) {
            const workletNode = new AudioWorkletNode(micCtx, "mic-processor");
            micProcessorRef.current = workletNode;

            workletNode.port.onmessage = (e) => {
                // Ref, not state: skips Deepgram sends while muted (see muteRef comment).
                if (muteRef.current) return;

                const dg = deepgramSocketRef.current;
                if (!dg || dg.readyState !== WebSocket.OPEN) return;

                // Track last audio byte time for latency calculations
                lastAudioByteTimeRef.current = Date.now();

                try {
                    dg.send(e.data);
                } catch (err) {
                    console.warn("Deepgram send error", err);
                }
            };

            micSourceRef.current.connect(workletNode);
        }

        console.log("[VoiceChat] Setting isRecording to true");
        setIsRecording(true);
        Cookie.setCookie('call_start', 'true')
    };

    // When StartOfTurn (user started speaking) - handle interruptions
    const handleStartOfTurn = () => {
        console.log("User started speaking");

        // Slam the gate shut immediately. Any in-flight TTS chunks for the
        // previous turn that arrive after this point will be dropped, even
        // before the next EndOfTurn mints a new valid turn id.
        obs.invalidateAiTurn();

        // Log turn detection latency
        if (lastAudioByteTimeRef.current) {
            const turnDetectionMs = Date.now() - lastAudioByteTimeRef.current;
            obs.emit(ObsEvent.DEEPGRAM_TURN_DETECTED, {
                turn_detection_ms: turnDetectionMs,
                status: 'success'
            });
        }

        if (isAISpeakingRef.current) {
            console.log("Interruption detected! Stopping AI...");
            stopTtsPlayback();
            clearTtsQueue();
            socket.emit("interrupt_ai_response", {});
        }
    };

    // When EndOfTurn (user finished speaking)
    const handleEndOfTurn = (transcript) => {
        console.log("User finished speaking:", transcript);

        // Calculate transcription latency
        const transcriptionMs = lastAudioByteTimeRef.current
            ? Date.now() - lastAudioByteTimeRef.current
            : null;

        // Generate turn_id and start turn for e2e tracking
        const turnId = obs.startTurn();

        // Reset first TTS chunk tracker for this new turn
        firstTtsChunkReceivedRef.current = false;

        // Log transcription complete
        obs.emit(ObsEvent.DEEPGRAM_TRANSCRIPTION_COMPLETE, {
            transcription_ms: transcriptionMs,
            turn_id: turnId,
            transcript_length: transcript?.length || 0,
            status: 'success'
        });

        socket.emit("send_voice_message", {
            transcript,
            chat_id: chatId,
            is_interruption: false,
            creator_id: userId,
            turn_id: turnId,
            turn_start_time: obs.getTurn().turn_start_time,
        });

        isAISpeakingRef.current = true;
        console.log("AI started speaking");
    };

    // TTS queue and playback
    const ensureTtsCtx = () => {
        if (!ttsCtxRef.current) {
            ttsCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return ttsCtxRef.current;
    };

    const queueTtsChunk = (base64, meta = {}) => {
        const { format, turn_id } = meta;

        // A new turn_id means we're past a barge-in or starting fresh; reset
        // the scheduling clock so the next chunk is anchored to currentTime,
        // and reset the first-chunk pre-buffer so the new turn gets its own
        // safe lead-time cushion instead of inheriting the previous turn's
        // (already-consumed) one.
        if (
            turn_id &&
            currentTurnIdRef.current &&
            turn_id !== currentTurnIdRef.current
        ) {
            nextStartTimeRef.current = 0;
            firstChunkBufferRef.current = new Uint8Array(0);
            firstChunkPendingRef.current = true;
        }
        if (turn_id) {
            currentTurnIdRef.current = turn_id;
        }
        ttsStreamEndedRef.current = false;

        if (format === 'pcm_s16le_24000') {
            schedulePcmChunk(base64);
            return;
        }

        decodingQueueRef.current = decodingQueueRef.current.then(async () => {
            const ttsCtx = ensureTtsCtx();

            const binary = atob(base64);
            const buffer = new ArrayBuffer(binary.length);
            const bytes = new Uint8Array(buffer);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            const decoded = await ttsCtx.decodeAudioData(buffer);

            ttsQueueRef.current.push(decoded);

            if (!ttsPlayingRef.current && ttsQueueRef.current.length === 1) {
                playNextTtsChunk();
            }
        }).catch((err) => {
            console.error("Decode queue error:", err);
        });
    };

    // Schedules already-decoded PCM bytes for gapless playback. Factored out
    // of schedulePcmChunk so the pre-buffer flush (below) can call it
    // directly with accumulated bytes instead of a fresh base64 chunk.
    const schedulePcmBytes = (bytes) => {
        try {
            const ttsCtx = ensureTtsCtx();
            if (ttsCtx.state === 'suspended') {
                ttsCtx.resume().catch(() => { });
            }

            // Re-interpret the byte buffer as little-endian int16 samples and
            // convert to float32 in [-1, 1] for AudioBuffer.
            const sampleCount = Math.floor(bytes.length / 2);
            if (sampleCount === 0) return;
            const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, sampleCount);
            const float32 = new Float32Array(sampleCount);
            for (let i = 0; i < sampleCount; i++) float32[i] = int16[i] / 32768;

            const audioBuffer = ttsCtx.createBuffer(1, sampleCount, 24000);
            audioBuffer.copyToChannel(float32, 0);

            const src = ttsCtx.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(ttsCtx.destination);

            const now = ttsCtx.currentTime;
            const when = Math.max(now + 0.02, nextStartTimeRef.current);
            nextStartTimeRef.current = when + audioBuffer.duration;

            scheduledSourcesRef.current.push(src);

            if (!isAISpeakingRef.current) {
                isAISpeakingRef.current = true;
                console.log("AI started speaking");
            }

            src.onended = () => {
                const arr = scheduledSourcesRef.current;
                const idx = arr.indexOf(src);
                if (idx !== -1) arr.splice(idx, 1);
                try { src.disconnect(); } catch (e) { }

                if (
                    arr.length === 0 &&
                    ttsStreamEndedRef.current &&
                    isAISpeakingRef.current
                ) {
                    isAISpeakingRef.current = false;
                    console.log("AI finished speaking");
                    obs.endTurn();
                }
            };

            src.start(when);
        } catch (err) {
            console.error("schedulePcmBytes error", err);
        }
    };

    // The backend now relays ElevenLabs' raw, irregularly-sized audio bursts
    // immediately with no smoothing -- that used to happen on the backend,
    // but doing it there AND here would double the latency before the fan
    // hears anything. So the frontend owns it now: for the first chunk of a
    // turn, accumulate incoming pieces until there's a safe amount buffered
    // (FIRST_CHUNK_MIN_BYTES) before scheduling anything, which absorbs the
    // kind of main-thread timing jitter that caused an audible stutter right
    // at the start of playback. Every chunk after that first one schedules
    // immediately -- by then nextStartTimeRef already has a lead-time
    // cushion built up, so there's no need to keep batching.
    const concatUint8 = (a, b) => {
        const out = new Uint8Array(a.length + b.length);
        out.set(a, 0);
        out.set(b, a.length);
        return out;
    };

    const schedulePcmChunk = (base64) => {
        const binary = atob(base64);
        let bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        if (firstChunkPendingRef.current) {
            firstChunkBufferRef.current = concatUint8(firstChunkBufferRef.current, bytes);
            if (firstChunkBufferRef.current.length < FIRST_CHUNK_MIN_BYTES) {
                return; // keep collecting before the first play of this turn
            }
            bytes = firstChunkBufferRef.current;
            firstChunkBufferRef.current = new Uint8Array(0);
            firstChunkPendingRef.current = false;
        }

        schedulePcmBytes(bytes);
    };

    // Flushes whatever's still sitting in the first-chunk accumulator, even
    // if it never reached FIRST_CHUNK_MIN_BYTES (a turn shorter than ~200ms,
    // e.g. a quick greeting) -- otherwise that trailing audio would never
    // play, and isAISpeaking could flip false while audio is still pending.
    const flushPendingFirstChunk = () => {
        if (firstChunkBufferRef.current.length > 0) {
            const bytes = firstChunkBufferRef.current;
            firstChunkBufferRef.current = new Uint8Array(0);
            firstChunkPendingRef.current = false;
            schedulePcmBytes(bytes);
        }
    };

    const playNextTtsChunk = () => {
        const q = ttsQueueRef.current;
        if (q.length === 0) {
            ttsPlayingRef.current = false;

            if (isAISpeakingRef.current) {
                isAISpeakingRef.current = false;
                console.log("AI finished speaking");
                obs.endTurn();
            }
            return;
        }

        const buf = q.shift();
        const ttsCtx = ensureTtsCtx();

        const src = ttsCtx.createBufferSource();
        src.buffer = buf;
        src.connect(ttsCtx.destination);

        const now = ttsCtx.currentTime;

        if (nextStartTimeRef.current < now) {
            nextStartTimeRef.current = now;
        }

        src.start(nextStartTimeRef.current);
        nextStartTimeRef.current += buf.duration;

        ttsPlayingRef.current = true;
        isAISpeakingRef.current = true;
        ttsCurrentSourceRef.current = src;

        src.onended = () => {
            ttsCurrentSourceRef.current = null;
            playNextTtsChunk();
        };
    };

    const stopTtsPlayback = () => {
        decodingQueueRef.current = Promise.resolve();

        try {
            if (ttsCurrentSourceRef.current) {
                try {
                    ttsCurrentSourceRef.current.onended = null;
                    ttsCurrentSourceRef.current.stop(0);
                    ttsCurrentSourceRef.current.disconnect();
                } catch (e) { }
                ttsCurrentSourceRef.current = null;
            }
            for (const src of scheduledSourcesRef.current) {
                try {
                    src.onended = null;
                    src.stop(0);
                    src.disconnect();
                } catch (e) { }
            }
            scheduledSourcesRef.current = [];
        } finally {
            ttsPlayingRef.current = false;
            isAISpeakingRef.current = false;
            nextStartTimeRef.current = 0;
            ttsStreamEndedRef.current = false;
            firstChunkBufferRef.current = new Uint8Array(0);
            firstChunkPendingRef.current = true;
            console.log("AI stopped speaking");
        }
    };

    const clearTtsQueue = () => {
        ttsQueueRef.current = [];
        nextStartTimeRef.current = 0;
        ttsStreamEndedRef.current = false;
        firstChunkBufferRef.current = new Uint8Array(0);
        firstChunkPendingRef.current = true;
        decodingQueueRef.current = Promise.resolve();
    };
    // Stop all and cleanup
    const stopAll = async (from = "") => {
        if (isStoppingRef.current) return false;
        isStoppingRef.current = true;
        console.log('Call stopping.....')
        Cookie.deleteCookie('call_start');
        setCallPhase("ending");
        setIsConnecting(false);
        setIsRecording(false);
        setMicReady(false);
        setDgReady(false);

        // Clean up everything FIRST before any async operations
        try {
            micProcessorRef.current?.disconnect();
            micProcessorRef.current = null;
            micSourceRef.current?.disconnect();
            micSourceRef.current = null;
        } catch (e) { }

        try {
            micCtxRef.current?.close();
            micCtxRef.current = null;
        } catch (e) { }

        try {
            audioStreamRef.current?.getTracks().forEach((t) => t.stop());
            audioStreamRef.current = null;
        } catch (e) { }

        try {
            deepgramSocketRef.current?.close();
            deepgramSocketRef.current = null;
        } catch (e) { }

        stopTtsPlayback();
        clearTtsQueue();
        currentTurnIdRef.current = null;
        try {
            ttsCtxRef.current?.close();
            ttsCtxRef.current = null;
        } catch (e) { }

        // Clear keep-alive interval
        if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }
        obs.emit(ObsEvent.AUDIO_CALL_ENDED, {
            duration_sec: callDuration,
            status: 'success'
        });

        // Play hangup sound AFTER cleanup (fire and forget)
        if (!document.hidden && from !== "permission_denied") {
            playAudioWithGlobalContext("/sounds/hangup.mp3")
                .then(audioControl => { hangupAudioRef.current = audioControl; })
                .catch(e => console.error("Hangup play error", e));
        }

        // setIsRecording(false);

        if (from == "limit_exceeded") {
            return false
        } else {
            socket.emit("end_audio_call", { creator_id: userId });
            setTimeout(() => {
                navigate(`/user/chat?id=${userId}`, { replace: true });
            }, from === "permission_denied" ? 0 : 250);
        }

    };

    // Re-assigned every render: Deepgram handlers and the background timer are created once
    // and call through this ref, so it must hold the NEWEST stopAll closure. Commenting this
    // out in an unfinished May refactor silently disabled three teardown paths.
    stopAllRef.current = stopAll;

    // Backend tears the media session down on Socket.IO disconnect. End the local call UI
    // immediately instead of pretending the call survived a silent socket resume.
    useEffect(() => {
        return onSocketDisconnect(() => {
            if (isStoppingRef.current) return;
            toast.error("Connection lost. Call ended.");
            const stop = stopAllRef.current;
            if (stop) stop("socket_disconnected");
        });
    }, []);

    const BACKGROUND_CALL_GRACE_MS = 30000;
    useEffect(() => {
        const clearTimer = () => {
            if (visibilityEndTimerRef.current) {
                clearTimeout(visibilityEndTimerRef.current);
                visibilityEndTimerRef.current = null;
            }
        };

        /** Tab in background, app switcher, or window lost focus (e.g. Alt+Tab on desktop). */
        const isCallOutOfFocus = () =>
            document.visibilityState === "hidden" || !document.hasFocus();

        const syncBackgroundTimer = () => {
            if (!isRecording) return;

            if (!isCallOutOfFocus()) {
                clearTimer();
                return;
            }

            clearTimer();
            visibilityEndTimerRef.current = setTimeout(() => {
                visibilityEndTimerRef.current = null;
                if (isCallOutOfFocus()) {
                    stopAllRef.current?.();
                }
            }, BACKGROUND_CALL_GRACE_MS);
        };

        const onWindowBlur = () => {
            setTimeout(syncBackgroundTimer, 0);
        };

        document.addEventListener("visibilitychange", syncBackgroundTimer);
        window.addEventListener("blur", onWindowBlur);
        window.addEventListener("focus", syncBackgroundTimer);
        window.addEventListener("pageshow", syncBackgroundTimer);

        return () => {
            document.removeEventListener("visibilitychange", syncBackgroundTimer);
            window.removeEventListener("blur", onWindowBlur);
            window.removeEventListener("focus", syncBackgroundTimer);
            window.removeEventListener("pageshow", syncBackgroundTimer);
            clearTimer();
        };
    }, [isRecording]);

    // Mute/unmute
    const handleMute = () => {
        setMute(true);
        // Keep the worklet-callback mirror in sync (state alone never reaches it).
        muteRef.current = true;
        try {
            if (audioStreamRef.current) {
                audioStreamRef.current.getAudioTracks().forEach(track => {
                    track.enabled = false;
                });
            }
            console.log("MIC MUTED");
        } catch (e) {
            console.error("Mute error:", e);
        }
    };

    const handleUnmute = () => {
        setMute(false);
        // Keep the worklet-callback mirror in sync (state alone never reaches it).
        muteRef.current = false;
        try {
            if (audioStreamRef.current) {
                audioStreamRef.current.getAudioTracks().forEach(track => {
                    track.enabled = true;
                });
            }
            console.log("MIC UNMUTED");
        } catch (e) {
            console.error("Unmute error:", e);
        }
    };

    // Request mic on mount
    useEffect(() => {
        console.log('userId && chatId', userId, chatId);
        let isMounted = true;

        const initializeVoiceCall = async () => {
            if (!userId || !chatId) return;

            // Without a live socket no token/greeting can ever arrive; bail out honestly
            // instead of leaving the user stuck on an eternal "Ringing..." screen.
            if (!socket) {
                toast.error("Connection not ready. Please try again.");
                navigate(`/user/chat?id=${userId}`, { replace: true });
                return;
            }

            setIsConnecting(true);
            const hasPermission = await requestMic();
            if (!isMounted) return;

            if (!hasPermission) {
                stopAll("permission_denied");
            }
        };

        initializeVoiceCall();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, chatId]);

    useEffect(() => {
        if (isBackPressed) {
            stopAll()
        }
    }, [isBackPressed])

    useEffect(() => {
        isStoppingRef.current = false;
    }, [userId, chatId]);

    const getCallStatusText = () => {
        if (isRecording) {
            return formatTime(callDuration);
        }

        switch (callPhase) {
            case "requesting_permissions":
                return "Waiting for microphone permission...";
            case "starting_call":
                return "Ringing...";
            case "connected":
                return "Connected";
            case "ending":
                return "Ending call...";
            default:
                return permissionStatus === "granted" && isConnecting ? "Ringing..." : "Connecting...";
        }
    };

    const showLoaderOverlay =
        permissionStatus === "pending";

    return (
        <>

            <PayrollModal id={userId} name={name} stopAll={() => navigate(`/user/chat?id=${userId}`, { replace: true })} />

            {/* ============== start: voice-call page ============== */}
            <div className="h-full gradient-bg-1 flex flex-col items-center justify-center p-6 relative">
                {showLoaderOverlay && (
                    <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-md">
                        <div className="loader" style={{ width: 36, height: 36 }} />
                        <p className="m-0 text-base font-medium text-white">{getCallStatusText()}</p>
                    </div>
                )}
                <div className="text-center" style={{ opacity: 1, transform: "none" }}>

                    <Avatar alt="User" src={profilePic}
                        className="w-48 h-48 rounded-full mx-auto mb-8 shadow-2xl border-4 border-card"
                        sx={{ width: 150, height: 150, fontSize: 30 }}
                    >
                        {name?.charAt(0)}
                    </Avatar>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        {name
                            ? <span className="name-fade-in">{name}</span>
                            : <span className="name-skeleton" style={{ width: 160, height: '1.5em' }} />}
                    </h2>
                    <p className="text-lg text-white mb-2">{getCallStatusText()}</p>
                    <div className="mb-8" />
                    {
                        callPhase !== "ending" && (
                            <div className="flex gap-6 justify-center voice-btns-enter">
                                {isRecording && (
                                    <button className="w-16 h-16 rounded-full flex items-center justify-center transition-all bg-card shadow-lg"
                                        onClick={() => {
                                            if (!mute) handleMute();
                                            else handleUnmute();
                                        }}
                                    >
                                        {!mute ? <MdKeyboardVoice fill="#030817" size={32} /> : <AiOutlineAudioMuted fill="#030817" size={32} />}
                                    </button>
                                )}
                                <button className="w-16 h-16 rounded-full bg-destructive flex items-center justify-center shadow-lg"
                                    onClick={() => stopAll()}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-phone w-6 h-6 text-white transform rotate-135"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                </button>
                            </div>)
                    }
                </div>
            </div>
            {/* ============== end: voice-call page ============== */}
        </>
    );
};

export default VoiceChat;
